import {
  AuthService,
  type AvatarAssetCacheInput,
  AvatarAssetCacheService,
  ServerService,
  toAbsoluteAvatarAssetUrl,
} from '@affine/core/modules/cloud';
import { MemberSearchService } from '@affine/core/modules/permissions';
import { highlighter } from '@affine/core/modules/quicksearch/utils/highlighter';
import { I18n } from '@affine/i18n';
import track from '@affine/track';
import type { AffineInlineEditor } from '@blocksuite/affine/shared/types';
import type {
  LinkedMenuItem,
  LinkedWidgetConfig,
} from '@blocksuite/affine/widgets/linked-doc';
import { BLOCK_ID_ATTR, type BlockComponent } from '@blocksuite/std';
import { computed } from '@preact/signals-core';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { FrameworkProvider } from '@toeverything/infra';
import Fuse, { type FuseResultMatch } from 'fuse.js';
import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { until } from 'lit/directives/until.js';

import { memberAvatarFallback } from '../../../utils/member-avatar-fallback';

export const createCommentLinkedWidgetConfig = (
  framework: FrameworkProvider
): Partial<LinkedWidgetConfig> | undefined => {
  const memberGroup = (
    query: string,
    close: () => void,
    inlineEditor: AffineInlineEditor,
    signal: AbortSignal
  ) => {
    const memberSearchService = framework.get(MemberSearchService);
    const avatarCache = framework.getOptional(AvatarAssetCacheService);
    const serverService = framework.getOptional(ServerService);
    const authService = framework.getOptional(AuthService);
    const memberSearchSession = memberSearchService.createSession();
    // Linked-widget aborts via signal on Escape/blur/unmount; explicit
    // user-action close also disposes for promptness. Both paths idempotent.
    const dispose = () => memberSearchSession.dispose();
    signal.addEventListener('abort', dispose, { once: true });
    const closeAndDispose = () => {
      dispose();
      close();
    };

    const getMenuItem = (
      id: string,
      name?: string | null,
      avatar?: string | null
    ): LinkedMenuItem => {
      const avatarStyle = styleMap({
        borderRadius: '50%',
        border: `1px solid ${cssVarV2('layer/background/overlayPanel')}`,
        width: '20px',
        height: '20px',
        boxSizing: 'border-box',
      });
      const liveIcon = avatar
        ? html`<img style=${avatarStyle} src=${avatar} />`
        : memberAvatarFallback(name);
      const account = authService?.session.account$.signal.value;
      const sourceUrl =
        avatar && serverService
          ? toAbsoluteAvatarAssetUrl(avatar, serverService.server.baseUrl)
          : null;
      let icon = liveIcon;
      if (avatarCache && serverService && account && sourceUrl) {
        const cacheInput: AvatarAssetCacheInput = {
          kind: 'user-avatar',
          scope: {
            accountId: account.id,
            serverId: serverService.server.id,
          },
          entityId: id,
          source: sourceUrl,
        };
        icon = html`${until(
          avatarCache
            .objectUrl(cacheInput, serverService.server.baseUrl)
            .then(objectUrl =>
              objectUrl
                ? html`<img
                    style=${avatarStyle}
                    src=${objectUrl}
                    @load=${() => URL.revokeObjectURL(objectUrl)}
                    @error=${() => URL.revokeObjectURL(objectUrl)}
                  />`
                : liveIcon
            )
            .catch(() => liveIcon),
          liveIcon
        )}`;
      }

      let displayName = name ?? 'Unknown';
      return {
        key: id,
        name: html`${unsafeHTML(displayName)}`,
        icon,
        action: () => {
          const root = inlineEditor.rootElement;
          const block = root?.closest<BlockComponent>(`[${BLOCK_ID_ATTR}]`);
          if (!block) return;

          closeAndDispose();

          track.doc.editor.atMenu.mentionMember({
            type: 'member',
          });

          const inlineRange = inlineEditor.getInlineRange();
          if (!inlineRange || inlineRange.length !== 0) return;

          inlineEditor.insertText(inlineRange, ' ', {
            mention: {
              member: id,
            },
          });
          inlineEditor.setInlineRange({
            index: inlineRange.index + 1,
            length: 0,
          });
        },
      };
    };
    const highlightFuseTitle = (
      matches: readonly FuseResultMatch[] | undefined,
      title: string,
      key: string
    ): string => {
      if (!matches) {
        return title;
      }
      const normalizedRange = ([start, end]: [number, number]) =>
        [
          start,
          end + 1 /* in fuse, the `end` is different from the `substring` */,
        ] as [number, number];
      const titleMatches = matches
        ?.filter(match => match.key === key)
        .flatMap(match => match.indices.map(normalizedRange));
      return (
        highlighter(
          title,
          `<span style="color: ${cssVarV2('text/emphasis')}">`,
          '</span>',
          titleMatches ?? []
        ) ?? title
      );
    };

    memberSearchSession.search(query);

    const items = computed<LinkedMenuItem[]>(() => {
      const members = memberSearchSession.result$.signal.value;

      if (query.length === 0) {
        return members
          .slice(0, 3)
          .map(member => getMenuItem(member.id, member.name, member.avatarUrl));
      }

      // Create a single Fuse instance for all members
      const fuse = new Fuse(members, {
        keys: ['name'],
        includeMatches: true,
        includeScore: true,
        ignoreLocation: true,
        threshold: 0.0,
      });
      const searchResults = fuse.search(query);

      return searchResults.map(result => {
        const member = result.item;
        const displayName = highlightFuseTitle(
          result.matches,
          member.name ?? 'Unknown',
          'name'
        );
        return {
          ...getMenuItem(member.id, member.name, member.avatarUrl),
          name: html`${unsafeHTML(displayName)}`,
        };
      });
    });

    return {
      name: I18n.t('com.streamlain.editor.at-menu.mention-members'),
      items,
      loading: memberSearchSession.isLoading$.signal,
      hidden: computed(() => {
        return (
          memberSearchSession.result$.signal.value.length === 0 &&
          !memberSearchSession.isLoading$.signal.value
        );
      }),
      maxDisplay: 3,
      overflowText: computed(() => {
        const totalCount = memberSearchSession.result$.signal.value.length;
        const remainingCount = totalCount - 3;
        return I18n.t('com.streamlain.editor.at-menu.more-members-hint', {
          count: remainingCount,
        });
      }),
    };
  };

  return {
    getMenus: (query, close, _editorHost, inlineEditor, signal) => {
      return [memberGroup(query, close, inlineEditor, signal)];
    },
  };
};
